/**
 * AccessModal — "Get Access" flow for visitors.
 *
 * - Visitor: browse public pages
 * - Developer: enter API key
 * - Operator: email/password login (JWT hidden behind product UX)
 */

import { useState } from 'react';
import { useAuth } from './AuthGate';

type Persona = 'choose' | 'developer' | 'operator';

interface AccessModalProps {
  open: boolean;
  onClose: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';

export default function AccessModal({ open, onClose }: AccessModalProps) {
  const { loginApiKey, loginJwt } = useAuth();
  const [persona, setPersona] = useState<Persona>('choose');
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [apiKey, setApiKey] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const reset = () => {
    setPersona('choose');
    setApiKey('');
    setEmail('');
    setPassword('');
    setRegName('');
    setAuthMode('login');
    setError('');
    setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleApiKeySubmit = () => {
    const trimmed = apiKey.trim();
    if (!trimmed) { setError('Please enter your API key'); return; }
    if (!trimmed.startsWith('nxm_')) { setError('API keys start with nxm_'); return; }
    loginApiKey(trimmed);
    handleClose();
  };

  const handleOperatorLogin = async () => {
    if (!email.trim() || !password) { setError('Email and password are required'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed'); return; }
      loginJwt(data.access_token, data.user, data.refresh_token);
      handleClose();
    } catch {
      setError('Unable to reach the server.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!email.trim() || !password) { setError('Email and password are required'); return; }
    setLoading(true);
    setError('');
    try {
      const body: Record<string, string> = { email: email.trim(), password };
      if (regName.trim()) body.name = regName.trim();
      const res = await fetch(`${API_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Registration failed'); return; }
      // Auto-login after successful registration
      if (data.access_token) {
        loginJwt(data.access_token, data.user, data.refresh_token);
        handleClose();
      } else {
        // Backend may return tokens differently — fall back to login
        setAuthMode('login');
        setError('Account created. Please sign in.');
      }
    } catch {
      setError('Unable to reach the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nx-modal-backdrop" onClick={handleClose} style={{ padding: '16px' }}>
      <div className="nx-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', width: '100%', boxSizing: 'border-box' }}>
        <button className="nx-modal__close" onClick={handleClose} aria-label="Close">&times;</button>

        {persona === 'choose' && (
          <>
            <h2 className="nx-modal__title">Get Access</h2>
            <p className="nx-text-dim nx-text-xs" style={{ marginBottom: '1rem' }}>
              Choose how you want to use Nexum Protocol.
            </p>
            <div className="nx-access-paths" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
              <button className="nx-access-card" onClick={handleClose}>
                <span className="nx-access-card__icon nx-access-card__icon--visitor">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} width={24} height={24}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <span className="nx-access-card__title">Visitor</span>
                <span className="nx-access-card__desc">Browse capabilities, search the catalog, read docs. No auth needed.</span>
              </button>

              <button className="nx-access-card" onClick={() => { setPersona('developer'); setError(''); }}>
                <span className="nx-access-card__icon nx-access-card__icon--developer">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} width={24} height={24}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </span>
                <span className="nx-access-card__title">Developer</span>
                <span className="nx-access-card__desc">Connect as an agent developer. Search capabilities and manage your API key.</span>
              </button>

              <button className="nx-access-card" onClick={() => { setPersona('operator'); setError(''); }}>
                <span className="nx-access-card__icon nx-access-card__icon--operator">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} width={24} height={24}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </span>
                <span className="nx-access-card__title">Operator</span>
                <span className="nx-access-card__desc">Govern agent execution. Review decisions, approve actions, inspect evidence.</span>
              </button>
            </div>
          </>
        )}

        {persona === 'developer' && (
          <>
            <button className="nx-modal__back" onClick={() => { setPersona('choose'); setError(''); }}>&larr; Back</button>
            <h2 className="nx-modal__title">Developer Access</h2>
            <p className="nx-text-dim nx-text-xs" style={{ marginBottom: '1rem' }}>
              Enter your API key to connect. Create an account first using the Operator path if you need credentials.
            </p>
            <div className="nx-modal__form" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input type="password" className="nx-input nx-input--lg" placeholder="nxm_your_api_key..." value={apiKey}
                onChange={(e) => { setApiKey(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleApiKeySubmit()} autoFocus
                style={{ width: '100%', boxSizing: 'border-box' }} />
              <button className="nx-btn nx-btn--primary" onClick={handleApiKeySubmit} style={{ width: '100%' }}>Connect</button>
            </div>
            {error && <div className="nx-text-red nx-text-xs" style={{ marginTop: 4 }}>{error}</div>}
          </>
        )}

        {persona === 'operator' && (
          <>
            <button className="nx-modal__back" onClick={() => { setPersona('choose'); setError(''); setAuthMode('login'); }}>&larr; Back</button>
            <h2 className="nx-modal__title">{authMode === 'login' ? 'Operator Login' : 'Create Account'}</h2>
            <p className="nx-text-dim nx-text-xs" style={{ marginBottom: '1rem' }}>
              {authMode === 'login'
                ? 'Sign in with your operator credentials.'
                : 'Create an operator account. You will be signed in automatically.'}
            </p>
            <div className="nx-modal__form" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {authMode === 'register' && (
                <input type="text" className="nx-input" placeholder="Name (optional)" value={regName}
                  onChange={(e) => { setRegName(e.target.value); setError(''); }} autoFocus
                  style={{ width: '100%', boxSizing: 'border-box' }} />
              )}
              <input type="email" className="nx-input" placeholder="Email" value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && (authMode === 'login' ? handleOperatorLogin() : handleRegister())}
                autoFocus={authMode === 'login'}
                style={{ width: '100%', boxSizing: 'border-box' }} />
              <input type="password" className="nx-input" placeholder="Password" value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && (authMode === 'login' ? handleOperatorLogin() : handleRegister())}
                style={{ width: '100%', boxSizing: 'border-box' }} />
              <button className="nx-btn nx-btn--primary" onClick={authMode === 'login' ? handleOperatorLogin : handleRegister} disabled={loading} style={{ width: '100%', marginTop: 4 }}>
                {loading ? (authMode === 'login' ? 'Signing in…' : 'Creating account…') : (authMode === 'login' ? 'Sign In' : 'Create Account')}
              </button>
            </div>
            {error && <div className="nx-text-red nx-text-xs" style={{ marginTop: 4 }}>{error}</div>}
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button
                onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
              >
                {authMode === 'login' ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
